import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Sparkles } from 'lucide-react';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { requestAnswerAnalysis } from '../api';
import { analysisLanguage } from '../language';
import { LockedInsight, insightsErrorCode } from 'modules/examInsights';

export interface AiAnalysisPanelProps {
  attemptId: string;
  questionId: string;
  /** A reading passage's sub-question, when `questionId` is the passage. */
  subQuestionId?: string;
  /** Analyses already saved for this answer, by language. */
  saved: Record<string, string>;
  className?: string;
}

/** The i18n key for what went wrong, from the API's error code. Anything unrecognised is a plain failure. */
function failureKey(error: unknown): string {
  const code = (error as { response?: { data?: { code?: unknown } } } | null)?.response?.data?.code;
  if (code === 'AI_ANALYSIS_LIMIT') return 'ai_analysis.limit';
  if (code === 'ANSWER_KEY_HIDDEN' || code === 'ANALYSIS_NOT_AVAILABLE') return 'ai_analysis.unavailable';
  return 'ai_analysis.failed';
}

/** "AI tahlil": for an answer with no explanation from the author, asks an AI to explain why it is right or wrong. */
export function AiAnalysisPanel({ attemptId, questionId, subQuestionId, saved, className }: AiAnalysisPanelProps) {
  const { t, i18n } = useTranslation();
  const language = analysisLanguage(i18n.language);
  const queryClient = useQueryClient();
  const key = ['answer-analysis', attemptId, questionId, subQuestionId ?? '', language];

  // Not fetched here: the text arrives from the result payload or from the mutation below, and is kept
  // under this key so moving to another question and back does not ask again.
  const { data: text } = useQuery<string | undefined>({
    queryKey: key,
    queryFn: () => Promise.resolve(saved[language]),
    initialData: saved[language],
    enabled: false,
    staleTime: Infinity,
  });

  // A ref, not `isPending`: two clicks in the same tick both see the old render.
  const inFlight = useRef(false);
  const analyse = useMutation({
    mutationFn: () => requestAnswerAnalysis(attemptId, questionId, language, subQuestionId),
    onSuccess: (result) => queryClient.setQueryData(key, result.analysis),
    onSettled: () => {
      inFlight.current = false;
    },
  });

  if (text) {
    return (
      <div
        className={cn(
          'mt-5 rounded-xl border border-primary-200 bg-primary-50 p-4 dark:border-primary/30 dark:bg-primary/10',
          className
        )}
      >
        <div className="flex items-center gap-2 text-[13px] font-semibold text-foreground">
          {/* Volt is a fill: the mark sits on a volt chip in ink, never as volt-on-tint. */}
          <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
          </span>
          <span>{t('ai_analysis.title')}</span>
        </div>
        <p className="mt-2.5 whitespace-pre-wrap border-t border-primary-200 pt-2.5 text-sm leading-relaxed text-foreground/90 dark:border-primary/20">
          {text}
        </p>
      </div>
    );
  }

  // A locked IELTS/SAT attempt whose one free analysis is already used.
  if (analyse.isError && insightsErrorCode(analyse.error) === 'PORTAL_PLAN_REQUIRED') {
    return <LockedInsight title={t('exam_insights.why_title')} description={t('exam_insights.why_body')} className={className} />;
  }

  return (
    <div className={cn('mt-5', className)}>
      <Button
        type="button"
        disabled={analyse.isPending}
        onClick={() => {
          if (inFlight.current) return;
          inFlight.current = true;
          analyse.mutate();
        }}
      >
        {analyse.isPending ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
        ) : (
          <Sparkles className="mr-2 h-4 w-4" aria-hidden />
        )}
        {analyse.isPending ? t('ai_analysis.working') : t('ai_analysis.button')}
      </Button>
      {analyse.isError ? (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {t(failureKey(analyse.error))}
        </p>
      ) : null}
    </div>
  );
}
